import { useMutation } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router";
import { IconDeviceDesktop } from "@tabler/icons-react";
import { DFY_PACKAGE_CREDITS } from "@repo/api/utils/dfy-billing";
import { paths } from "@/App";
import { Button } from "@/lib/components/Button/Button";
import { useSidebar } from "@/lib/components/Sidebar";
import { canEnlargeWindow } from "@/lib/use-mobile";
import Toast from "@/lib/components/Toast";
import { EditorApi } from "@/lib/api/editor-api";
import { AnalyticsApi } from "@/lib/api/analytics-api";
import { useTeamQuery } from "@/lib/queries/team-queries";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/lib/components/Dialog";
import {
  DFY_PACKAGE_EXAMPLES,
  formatDfyCreditsToPrice,
} from "../DoneForYou/OrderForm/dfy-order-form-content";
import DfyExampleVideo from "../DoneForYou/OrderForm/components/DfyExampleVideo";
import { useStartDfyOrderMutation } from "../DoneForYou/dfy-order-hooks";

export default function ActionCards() {
  const { teamExternalId } = useParams();
  const { data: team } = useTeamQuery(teamExternalId!);

  return (
    <div className="flex flex-col-reverse gap-6 md:flex-row">
      <CreateVideoCard />
      {team && !team.isDfyEditorTeam && <DfyCard />}
    </div>
  );
}

function Card({
  className,
  rings,
  artwork,
  children,
}: {
  className: string;
  rings: { src: string; className: string };
  artwork: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div
      className={`@container relative flex min-h-53.5 w-full min-w-0 overflow-hidden rounded-xl border transition-transform duration-200 hover:scale-101 md:max-w-125 md:flex-1 ${className}`}
    >
      <img
        src={rings.src}
        alt=""
        className={`pointer-events-none absolute size-160 max-w-none ${rings.className}`}
      />
      {children}
      {/* Artwork is sized in em: 1em is 1px at the 228px design width and shrinks with the box. */}
      <div className="@container pointer-events-none relative hidden min-w-0 flex-1 @min-[420px]:block">
        <div className="absolute inset-0 text-[length:min(1px,calc(100cqw/228))]">
          {artwork}
        </div>
      </div>
    </div>
  );
}

function CardText({
  title,
  description,
  children,
}: {
  title: string;
  description: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="relative z-10 my-10 ml-8 flex w-60 shrink-0 flex-col items-start gap-6">
      <div className="flex flex-col gap-1.5">
        <h2 className="text-lg font-bold">{title}</h2>
        <p className="text-sm text-text-secondary">{description}</p>
      </div>
      {children}
    </div>
  );
}

function CreateVideoCard() {
  const { teamExternalId } = useParams();
  const navigate = useNavigate();
  const { isMobile } = useSidebar();

  const createVideoMutation = useMutation({
    mutationFn: () =>
      EditorApi.createVideo(teamExternalId!, { source: "HOME" }),
    onSuccess: (video) => {
      navigate(paths.videoEdit(teamExternalId!, video.externalId));
    },
    onError: () => Toast.error("Failed to create a video"),
  });

  return (
    <Card
      className="border-primary-400/7 bg-linear-156 from-primary-100 to-secondary-100"
      rings={{
        src: "/home/rings-create.svg",
        className: "-top-30 left-21.5",
      }}
      artwork={<AnimationsMockup />}
    >
      <CardText
        title="Create video yourself"
        description="Turn your listing photos into stunning videos in minutes."
      >
        {isMobile ? (
          <p className="flex items-center gap-2 text-sm font-medium">
            <IconDeviceDesktop className="size-5 shrink-0" />
            {canEnlargeWindow()
              ? "Make your browser window larger to create videos"
              : "Video editor is available on desktop only"}
          </p>
        ) : (
          <Button
            size="sm"
            isLoading={createVideoMutation.isPending}
            onClick={() => createVideoMutation.mutate()}
          >
            Create video
          </Button>
        )}
      </CardText>
    </Card>
  );
}

function DfyCard() {
  const { teamExternalId } = useParams();
  const createOrderMutation = useStartDfyOrderMutation({
    teamExternalId: teamExternalId!,
    source: "HOME",
  });

  const price = formatDfyCreditsToPrice(DFY_PACKAGE_CREDITS.SMALL);

  return (
    <Card
      className="border-tertiary-400/5 bg-tertiary-50"
      rings={{
        src: "/home/rings-dfy.svg",
        className: "-top-32.5 left-34",
      }}
      artwork={
        <img
          src="/home/dfy-people.webp"
          alt=""
          className="absolute -right-[47em] bottom-0 h-[192em] w-[282em] max-w-none"
        />
      }
    >
      <CardText
        title="Done-for-You"
        description={
          <>
            Let us create your video.
            <br />
            Ready in 24 hours, from {price}.
          </>
        }
      >
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="tertiary"
            isLoading={createOrderMutation.isPending}
            onClick={() => createOrderMutation.mutate()}
          >
            Order video
          </Button>
          <DfyExamplesDialog />
        </div>
      </CardText>
    </Card>
  );
}

function DfyExamplesDialog() {
  const { teamExternalId } = useParams();

  return (
    <Dialog
      onOpenChange={(open) =>
        open && AnalyticsApi.logDfyExamplesDialogOpened(teamExternalId)
      }
    >
      <DialogTrigger asChild>
        <Button
          size="sm"
          variant="ghost"
          className="px-3 text-xs font-medium underline hover:bg-tertiary-100"
        >
          Examples
        </Button>
      </DialogTrigger>
      <DialogContent
        className="max-h-[min(100%,560px)] w-200 max-w-full gap-0"
        classNameInner="flex flex-col gap-0 overflow-y-auto p-0 sm:px-0"
        showCloseButton={false}
      >
        <DialogHeader variant="bordered" showCloseButton>
          <DialogTitle>Done-for-You examples</DialogTitle>
        </DialogHeader>
        <div className="min-h-0 overflow-y-auto px-7 py-6">
          <div className="grid gap-4 sm:grid-cols-2">
            {DFY_PACKAGE_EXAMPLES.map((example) => (
              <DfyExampleVideo
                key={example.vimeoId}
                example={example}
                source="HOME"
              />
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AnimationsMockup() {
  return (
    <img
      src="/home/animations-mockup.jpg"
      alt=""
      className="absolute top-[44.7em] -right-[136.9em] h-[232.8em] w-[344.4em] max-w-none rotate-4 rounded-[8.4em] border-[0.6em] border-border object-cover shadow-[-2px_2px_8px_0px_rgba(215,137,59,0.1),-12px_10px_60px_0px_rgba(166,100,34,0.15)]"
    />
  );
}
